import React, { useEffect, useState } from 'react'
import { Check, Heart, Shield, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface IslamStep {
  id: number
  icon: React.ReactNode
  title: string
  description: string
  details: string[]
  importance: string
  image?: string
  bgColor: string
  textColor: string
}

export default function EnteringIslamSteps() {
  const { t, i18n } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)
  const [autoplayPaused, setAutoplayPaused] = useState(false)
  const currentLang = i18n.language || 'ar'
  const isRTL = currentLang === 'ar'

  const enteringIslamSteps: IslamStep[] = [
    {
      id: 1,
      icon: <Heart className="w-8 h-8" />,
      title: t('islam.entering.step1.title', 'Uttering the two testimonies'),
      description: t('islam.entering.step1.desc', 'The most important step and the foundation of embracing Islam'),
      importance: t('islam.entering.step1.importance', 'Obligatory: First pillar'),
      details: [
        t('islam.entering.step1.detail1', 'I bear witness that there is no god but Allah'),
        t('islam.entering.step1.detail2', 'And I bear witness that Muhammad is His messenger'),
        t('islam.entering.step1.detail3', 'Uttered with sincerity and devotion'),
        t('islam.entering.step1.detail4', 'Preferably in the presence of Muslim witnesses'),
        t('islam.entering.step1.detail5', 'Can be pronounced in any language, then learn Arabic'),
      ],
      image: '/assets/Islam_steps/image 3.png',
      bgColor: 'bg-gray-900',
      textColor: 'text-white',
    },
    {
      id: 2,
      icon: <Shield className="w-8 h-8" />,
      title: t('islam.entering.step2.title', 'Pillars of faith'),
      description: t('islam.entering.step2.desc', 'Belief in the six pillars of faith'),
      importance: t('islam.entering.step2.importance', 'Obligatory - Foundation of faith'),
      details: [
        t('islam.entering.step2.detail1', 'Belief in Allah alone with no partner'),
        t('islam.entering.step2.detail2', 'Belief in the angels'),
        t('islam.entering.step2.detail3', 'Belief in the revealed books (Quran is the last)'),
        t('islam.entering.step2.detail4', 'Belief in the messengers (Muhammad is the last)'),
        t('islam.entering.step2.detail5', 'Belief in the Last Day'),
        t('islam.entering.step2.detail6', 'Belief in divine decree, its good and bad'),
      ],
      image: '/assets/Islam_steps/image 4.png',
      bgColor: 'bg-[#E8F5E9]',
      textColor: 'text-gray-900',
    },
    {
      id: 3,
      icon: <Shield className="w-8 h-8" />,
      title: t('islam.entering.step3.title', 'Ghusl (full ablution)'),
      description: t('islam.entering.step3.desc', 'Full purification after declaring the Shahada'),
      importance: t('islam.entering.step3.importance', 'Strongly recommended'),
      details: [
        t('islam.entering.step3.detail1', 'Full bath with the intention of purification'),
        t('islam.entering.step3.detail2', 'Starting by washing hands three times'),
        t('islam.entering.step3.detail3', 'Complete wudu'),
        t('islam.entering.step3.detail4', 'Washing head and hair with water'),
        t('islam.entering.step3.detail5', 'Washing the whole body starting from the right side'),
        t('islam.entering.step3.detail6', 'A Muslim sister or brother can help if needed'),
      ],
      image: '/assets/Islam_steps/image 3 (1).png',
      bgColor: 'bg-[#E8F5E9]',
      textColor: 'text-gray-900',
    },
    {
      id: 4,
      icon: <Shield className="w-8 h-8" />,
      title: t('islam.entering.step4.title', 'Learning the basics of worship'),
      description: t('islam.entering.step4.desc', 'Starting to learn prayer and wudu'),
      importance: t('islam.entering.step4.importance', 'Obligatory gradually'),
      details: [
        t('islam.entering.step4.detail1', 'Learning how to perform wudu (minor purification)'),
        t('islam.entering.step4.detail2', 'Learning prayer step by step'),
        t('islam.entering.step4.detail3', 'Memorizing Surah Al-Fatiha and short surahs'),
        t('islam.entering.step4.detail4', 'Knowing the five daily prayer times'),
        t('islam.entering.step4.detail5', 'Do not worry, learning is gradual and comfortable'),
        t('islam.entering.step4.detail6', 'The angels rejoice at every step you take'),
      ],
      image: '/assets/Islam_steps/image 4 (1).png',
      bgColor: 'bg-[#E8F5E9]',
      textColor: 'text-gray-900',
    },
    {
      id: 5,
      icon: <Shield className="w-8 h-8" />,
      title: t('islam.entering.step5.title', 'Joining the Muslim community'),
      description: t('islam.entering.step5.desc', 'Connecting with Muslims and learning from them'),
      importance: t('islam.entering.step5.importance', 'Very important'),
      details: [
        t('islam.entering.step5.detail1', 'Visiting the mosque or Islamic center'),
        t('islam.entering.step5.detail2', 'Getting to know brothers and sisters in faith'),
        t('islam.entering.step5.detail3', 'Attending new Muslim classes'),
        t('islam.entering.step5.detail4', 'Seeking support and help when needed'),
        t('islam.entering.step5.detail5', 'The Muslim community is your new family'),
      ],
      image: '/assets/Islam_steps/image 3 (2).png',
      bgColor: 'bg-[#E8F5E9]',
      textColor: 'text-gray-900',
    },
    {
      id: 6,
      icon: <Shield className="w-8 h-8" />,
      title: t('islam.entering.step6.title', 'Obtaining a certificate of Islam'),
      description: t('islam.entering.step6.desc', 'An official document proving your embrace of Islam'),
      importance: t('islam.entering.step6.importance', 'Recommended for official procedures'),
      details: [
        t('islam.entering.step6.detail1', 'Visiting an accredited Islamic center'),
        t('islam.entering.step6.detail2', 'Declaring the Shahada before witnesses'),
        t('islam.entering.step6.detail3', 'Obtaining an attested certificate'),
        t('islam.entering.step6.detail4', 'Useful for Hajj and Umrah later'),
        t('islam.entering.step6.detail5', 'Useful for Islamic marriage'),
        t('islam.entering.step6.detail6', 'Official documentation of your embrace of Islam'),
      ],
      image: '/assets/Islam_steps/image 4.png',
      bgColor: 'bg-[#E8F5E9]',
      textColor: 'text-gray-900',
    },
  ]

  const n = enteringIslamSteps.length

  useEffect(() => {
    if (autoplayPaused || currentStep >= n - 1) return
    const id = window.setInterval(() => {
      setCurrentStep((i) => Math.min(i + 1, n - 1))
    }, 5500)
    return () => window.clearInterval(id)
  }, [autoplayPaused, currentStep, n])

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % enteringIslamSteps.length)
  }

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + enteringIslamSteps.length) % enteringIslamSteps.length)
  }

  const goToStep = (index: number) => {
    setCurrentStep(index)
  }

  const slidePct = 100 / n

  return (
    <div>
      {/* Entering Islam Steps Section Container */}
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header - Figma Design */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontWeight: 700 }}>
            {t('islam.entering.title', 'Steps to embracing Islam')}
          </h2>
          <p className="text-sm md:text-base max-w-3xl mx-auto leading-relaxed" style={{ color: '#6B7280' }}>
            {t('islam.entering.subtitle', 'When you feel complete peace and decide to embrace Islam, here are the simple practical steps. Every step is blessed and made easy by Allah\'s leave.')}
          </p>
        </div>

        {/* Horizontal track: whole step pair slides left/right when currentStep changes */}
        <div className="mb-6 w-full overflow-hidden" dir="ltr">
          <div
            className="flex will-change-transform transition-transform duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0"
            style={{
              width: `${n * 100}%`,
              transform: `translateX(-${slidePct * currentStep}%)`,
            }}
          >
            {enteringIslamSteps.map((_, slideIndex) => {
              const main = enteringIslamSteps[slideIndex]
              const peek = enteringIslamSteps[(slideIndex + 1) % n]
              return (
                <div
                  key={slideIndex}
                  className={`flex shrink-0 flex-col items-start gap-4 md:flex-row ${isRTL ? 'md:flex-row-reverse' : ''}`}
                  style={{ width: `${slidePct}%` }}
                >
                  <div
                    className="relative w-full flex-shrink-0 overflow-hidden rounded-2xl bg-gray-900 text-white md:h-[550px] md:w-[65%]"
                    style={{
                      minHeight: '420px',
                      borderRadius: '20px',
                      boxShadow: '0 10px 24px rgba(16, 24, 40, 0.10)',
                      border: '1px solid rgba(16, 24, 40, 0.04)',
                    }}
                  >
                    {main.image && (
                      <div className="absolute inset-0">
                        <img
                          src={main.image}
                          alt={main.title}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <div className="absolute inset-0 bg-black/60" />
                      </div>
                    )}
                    <div className="relative z-10 flex h-full flex-col p-8">
                      <div className="mb-4">
                        <span className="inline-block rounded-full bg-white/20 px-3 py-1 text-sm font-medium backdrop-blur">
                          {main.importance}
                        </span>
                      </div>
                      <div className="mt-auto">
                        <div className="mb-4 flex items-center gap-4 md:mb-6">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                            {React.cloneElement(main.icon as React.ReactElement, { className: 'w-7 h-7' })}
                          </div>
                          <div className="text-5xl font-bold">{main.id}</div>
                        </div>
                        <h3 className="mb-2 text-2xl font-bold">{main.title}</h3>
                        <p className="mb-4 text-base opacity-90">{main.description}</p>
                        <ul className="space-y-2 md:space-y-3">
                          {main.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                              <span className="opacity-90">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div
                    className="relative hidden w-full flex-shrink-0 overflow-hidden rounded-2xl bg-[#F4FBF7] md:block md:h-[550px] md:w-[35%]"
                    style={{
                      minHeight: '420px',
                      borderRadius: '20px',
                      boxShadow: '0 10px 24px rgba(16, 24, 40, 0.10)',
                      border: '1px solid rgba(16, 24, 40, 0.04)',
                    }}
                  >
                    {peek.image && (
                      <div className="absolute inset-0">
                        <img
                          src={peek.image}
                          alt={peek.title}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <div className="absolute inset-0 bg-white/70" />
                      </div>
                    )}
                    <div className="relative z-20 flex h-full flex-col p-6 md:p-8">
                      <div className="mb-4">
                        <span
                          className="inline-block rounded-full bg-gradient-to-r from-[#16a34a] to-[#22c55e] px-3 py-1 text-sm font-medium text-white"
                          style={{ borderRadius: '9999px' }}
                        >
                          {peek.importance}
                        </span>
                      </div>
                      <div className="mt-0">
                        <div className="mb-4 flex items-center gap-4 md:mb-6">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-[#16a34a] to-[#22c55e] text-white">
                            {React.cloneElement(peek.icon as React.ReactElement, { className: 'w-7 h-7' })}
                          </div>
                          <div className="text-5xl font-bold text-[#16a34a]" style={{ fontWeight: 700 }}>
                            {peek.id}
                          </div>
                        </div>
                        <h3 className="mb-2 text-2xl font-bold" style={{ color: '#111827', fontWeight: 700 }}>
                          {peek.title}
                        </h3>
                        <p className="mb-4 text-base" style={{ color: '#6B7280' }}>
                          {peek.description}
                        </p>
                        <ul className="space-y-2 md:space-y-3">
                          {peek.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#16a34a]" />
                              <span className="font-medium leading-relaxed" style={{ color: '#6B7280' }}>
                                {detail}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Navigation — match reference: light prev + black dots + pause + green next (thin dark icons) */}
        <div
          className="mb-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          dir="ltr"
        >
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`inline-flex h-11 min-h-[44px] min-w-[4.75rem] items-center justify-center rounded-md border px-5 shadow-sm transition-all sm:h-10 sm:min-h-0 ${
              currentStep === 0
                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-600 dark:bg-gray-800'
                : 'border-gray-200/90 bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label={t('common.previous', 'Previous')}
          >
            <ChevronLeft className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </button>

          <div className="flex items-center gap-2 sm:gap-2.5 px-1" role="tablist" aria-label={t('common.slides', 'Slides')}>
            {enteringIslamSteps.map((_, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={index === currentStep}
                onClick={() => goToStep(index)}
                className="rounded-full p-1.5 transition-opacity hover:opacity-80"
                aria-label={`${t('common.slide', 'Slide')} ${index + 1}`}
              >
                <span
                  className={`block h-2 w-2 rounded-full ${
                    index === currentStep
                      ? 'bg-gray-900 dark:bg-gray-100'
                      : 'bg-gray-400 dark:bg-gray-500'
                  }`}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setAutoplayPaused((p) => !p)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-900 shadow-sm transition-colors hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500"
            aria-label={
              autoplayPaused
                ? t('common.playAutoplay', 'Resume slideshow')
                : t('common.pauseAutoplay', 'Pause slideshow')
            }
          >
            {autoplayPaused ? (
              <Play className="ml-0.5 h-3.5 w-3.5" strokeWidth={1.75} />
            ) : (
              <Pause className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
          </button>

          <button
            type="button"
            onClick={nextStep}
            disabled={currentStep >= n - 1}
            className={`inline-flex h-11 min-h-[44px] min-w-[4.75rem] items-center justify-center rounded-md border border-primary-700/20 px-5 shadow-sm transition-all sm:h-10 sm:min-h-0 ${
              currentStep >= n - 1
                ? 'cursor-not-allowed border-primary-600/20 bg-primary-600/35 text-gray-600'
                : 'bg-primary-600 text-gray-900 hover:bg-primary-700 active:bg-primary-800 dark:border-primary-400/30 dark:bg-primary-500 dark:text-gray-950 dark:hover:bg-primary-600'
            }`}
            aria-label={t('common.next', 'Next')}
          >
            <ChevronRight className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  )
}
